import { useGroupEventBus } from "$root/event-bus";
import { platformToast } from "$root/notification-system/toast";
import { seconds } from "$root/utils";
import { parallelize } from "$root/utils/promise";
import { type EventInvestmentType, type EventInvestmentUpdate, InvestmentStatus } from "@eu.mdotm.api/sphere";
import { readAtom, selectAtom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { unreachable } from "@mdotm/utils/runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { isTransientInvestmentStatus, isUpdatableInvestmentStatus } from "../common";
import { resetComposition, syncComposition } from "../components/composition-editor/persistence";
import { type PortfolioLabContextProps, type PortfolioLabInvestmentItem, PortfolioLabContext } from "../ctx";
import { portfolioLabRepository } from "../repository";

/**
 * Safety net for missed SSEs: while any watched item is transient or settling, the summaries are
 * also re-fetched on this interval, so a lost event can never wedge a column in a stale status.
 */
const STATUS_POLL_INTERVAL = seconds(30);

const SETTLE_ERROR_TOAST =
	"The portfolio calculation failed. Your lab changes are kept — the virtual copy was not re-synced.";

/**
 * Only real portfolios and proposals emit the `investment-update` SSE this hook consumes —
 * benchmarks/references keep their click-time-only status check.
 */
export function isWatchedInvestment(item: Pick<PortfolioLabInvestmentItem, "type">): boolean {
	return item.type === "INVESTMENT" || item.type === "INVESTMENT_PROPOSAL";
}

/**
 * The `investment-update` event types that carry a real portfolio's status.
 *
 * `PORTFOLIO_UPDATE` is the recompute of an existing portfolio, but the backend labels the
 * recompute of a freshly uploaded or duplicated one `PORTFOLIO_UPLOAD`, and an imported one
 * `PORTFOLIO_IMPORT` — same entity, same CALCULATING → READY/ERROR transitions, different `type`
 * string. Matching `PORTFOLIO_UPDATE` alone therefore dropped the status feed for exactly the
 * portfolios most likely to still be calculating when the lab opens them, leaving the column
 * wedged on its load-time status until the ~30s polling fallback caught up (MDOT-24442). Applies
 * to UI-driven uploads/duplicates just as much as to bot-driven ones.
 */
const PORTFOLIO_STATUS_EVENT_TYPES: ReadonlySet<EventInvestmentType> = new Set<EventInvestmentType>([
	"PORTFOLIO_UPDATE",
	"PORTFOLIO_UPLOAD",
	"PORTFOLIO_IMPORT",
]);

/** Whether an `investment-update` event carries a real portfolio's status. */
export function isPortfolioStatusEvent(event: Pick<EventInvestmentUpdate, "type">): boolean {
	return event.type != null && PORTFOLIO_STATUS_EVENT_TYPES.has(event.type);
}

const INVESTMENT_STATUS_VALUES: ReadonlySet<string> = new Set<string>(Object.values(InvestmentStatus));

/**
 * Narrows a summary's status (typed loosely across the per-entity summary endpoints) to the
 * `InvestmentStatus` union the lab guards understand. `null` when missing or unknown — the caller
 * must then keep the last known status rather than corrupt the item.
 */
export function narrowInvestmentStatus(status: string | undefined): InvestmentStatus | null {
	if (status != null && INVESTMENT_STATUS_VALUES.has(status)) {
		return status as InvestmentStatus;
	}
	return null;
}

/**
 * Resolves which watched lab columns an SSE batch refers to. Matches on the REAL `uuid` and
 * returns `virtualUuid`s: an investment and its proposal loaded side by side share the same real
 * uuid, so a single event fans out to every matching item (each then refetches the summary with
 * its own type, applying `enhanced: true` for the proposal).
 */
export function resolveWatchedVirtualUuids(
	investments: Pick<PortfolioLabInvestmentItem, "type" | "uuid" | "virtualUuid">[],
	eventUuids: ReadonlySet<string>,
): Set<string> {
	return new Set(investments.filter((i) => isWatchedInvestment(i) && eventUuids.has(i.uuid)).map((i) => i.virtualUuid));
}

export type SettleAction =
	/** Not waiting for a commit to settle — only the live status write applies. */
	| "none"
	/** Still transient — keep waiting for the next transition. */
	| "keep-waiting"
	/** Settled on an updatable status — re-sync the virtual copy (reset + re-push) and dirty the charts. */
	| "resync"
	/** Settled on ERROR — surface the failure, keep the lab state, skip the re-sync. */
	| "abort-with-toast"
	/** Settled on a terminal, non-updatable status (DRAFT/REVIEW) — stop waiting, skip the re-sync. */
	| "abort";

/** Pure settle machine for a refreshed real-investment status. */
export function resolveSettleAction(
	item: Pick<PortfolioLabInvestmentItem, "commitSettleStatus">,
	freshStatus: InvestmentStatus,
): SettleAction {
	if (item.commitSettleStatus !== "waiting") {
		return "none";
	}
	if (isUpdatableInvestmentStatus(freshStatus)) {
		return "resync";
	}
	if (isTransientInvestmentStatus(freshStatus)) {
		return "keep-waiting";
	}
	return freshStatus === "ERROR" ? "abort-with-toast" : "abort";
}

/** Whether the polling fallback must be active for this set of investments. */
export function shouldPollStatuses(
	investments: Pick<PortfolioLabInvestmentItem, "type" | "status" | "commitSettleStatus">[],
): boolean {
	return investments.some(
		(i) => isWatchedInvestment(i) && (isTransientInvestmentStatus(i.status) || i.commitSettleStatus === "waiting"),
	);
}

/**
 * Keeps each watched lab column's REAL investment status live, and settles deferred
 * Update-composition commits:
 *
 * - `investment-update` SSEs (the portfolio status types, matched on the real uuid) are a TRIGGER only —
 *   status truth always comes from a debounced `portfolioLabRepository.summary` refetch
 *   (invalidate + fetch), mirroring the PortfolioDetails pattern.
 * - The fresh status is written into `investments$` so `canTriggerUpdateComposition` (menu gating)
 *   and the header's "Calculating…" indicator react live.
 * - Items with `commitSettleStatus: "waiting"` run the settle machine (`resolveSettleAction`): on
 *   an updatable status the virtual copy is re-synced on this investment's write lane — reset to the
 *   just-saved source, then re-push of the CURRENT client composition so edits made during the
 *   wait survive. Both writes move the column's revision, so its charts read as stale (blur overlay
 *   + toolbar reload, manual refresh). On ERROR the re-sync is skipped and the failure is toasted; the lab state is kept.
 * - A ~30s polling fallback runs while anything is transient or settling, so a missed SSE can
 *   never wedge a column.
 *
 * Every write lands in `investments$`, which the column header subscribes to — the "Calculating…"
 * pill repaints from the atom, with no imperative `refreshHeader()` (that would remount the header
 * and close an open column menu; see LabPortfolioGroupHeader).
 */
export function useRealInvestmentStatusSync(): void {
	const ctx = useMandatoryContext(PortfolioLabContext);

	const refreshStatuses = useCallback(
		async (virtualUuids?: ReadonlySet<string>) => {
			const targets = readAtom(ctx.investments$).filter(
				(i) => isWatchedInvestment(i) && (virtualUuids == null || virtualUuids.has(i.virtualUuid)),
			);
			await parallelize(
				targets.map((target) => async () => {
					try {
						// Invalidate first so the refetch can never serve a stale cached summary, then
						// re-read the item at apply time — it may have changed (or been removed) meanwhile.
						portfolioLabRepository.invalidate([target.uuid]);
						const summary = await portfolioLabRepository.summary.fetchQuery(target.uuid, target.type);
						const freshStatus = narrowInvestmentStatus(summary.status);
						if (freshStatus != null) {
							applyRefreshedStatus(ctx, target.virtualUuid, freshStatus);
						}
					} catch {
						// A failed refresh is not actionable here — the SSE retrigger or the polling
						// fallback will retry; the click-time precondition check stays the final gate.
					}
				}),
			);
		},
		[ctx],
	);

	useGroupEventBus(
		"investment-update",
		(events) => {
			const eventUuids = new Set(events.flatMap((e) => (e.uuid != null ? [e.uuid] : [])));
			const virtualUuids = resolveWatchedVirtualUuids(readAtom(ctx.investments$), eventUuids);
			if (virtualUuids.size > 0) {
				void refreshStatuses(virtualUuids);
			}
		},
		{
			timeout: seconds(2),
			filter: (e) =>
				isPortfolioStatusEvent(e) &&
				e.uuid != null &&
				resolveWatchedVirtualUuids(readAtom(ctx.investments$), new Set([e.uuid])).size > 0,
		},
	);

	// Catch-up sweep: refresh every watched item once when it appears (initial load or a later
	// add), so a column that is already CALCULATING at load is guarded from the start — not only
	// after its next SSE transition.
	const [watchedKey$] = useState(() =>
		selectAtom(ctx.investments$, (items) =>
			items
				.filter(isWatchedInvestment)
				.map((i) => i.virtualUuid)
				.join("|"),
		),
	);
	const watchedKey = useAtomValue(watchedKey$);
	const sweptVirtualUuidsRef = useRef<Set<string>>(new Set());
	// biome-ignore lint/correctness/useExhaustiveDependencies: <watchedKey is an intentional re-run trigger; the body reads the live atom>
	useEffect(() => {
		const watched = readAtom(ctx.investments$).filter(isWatchedInvestment);
		const fresh = watched.filter((i) => !sweptVirtualUuidsRef.current.has(i.virtualUuid));
		for (const item of fresh) {
			sweptVirtualUuidsRef.current.add(item.virtualUuid);
		}
		if (fresh.length > 0) {
			void refreshStatuses(new Set(fresh.map((i) => i.virtualUuid)));
		}
	}, [watchedKey, ctx.investments$, refreshStatuses]);

	// Polling fallback (missed-SSE safety net): active only while something is transient/settling.
	const [shouldPoll$] = useState(() => selectAtom(ctx.investments$, shouldPollStatuses));
	const shouldPoll = useAtomValue(shouldPoll$);
	useEffect(() => {
		if (!shouldPoll) {
			return;
		}
		const intervalId = setInterval(() => void refreshStatuses(), STATUS_POLL_INTERVAL);
		return () => clearInterval(intervalId);
	}, [shouldPoll, refreshStatuses]);
}

/**
 * Applies a refreshed real-investment status to the lab item: writes the live status and runs the
 * settle machine's side effects. The header repaints from `investments$` on its own.
 */
function applyRefreshedStatus(ctx: PortfolioLabContextProps, virtualUuid: string, freshStatus: InvestmentStatus): void {
	const item = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
	if (!item) {
		return;
	}

	const patchItem = (patch: Partial<PortfolioLabInvestmentItem>): void => {
		writeAtom(ctx.investments$, (items) => items.map((i) => (i.virtualUuid === virtualUuid ? { ...i, ...patch } : i)));
	};

	const action = resolveSettleAction(item, freshStatus);
	switch (action) {
		case "none":
		case "keep-waiting": {
			if (item.status !== freshStatus) {
				patchItem({ status: freshStatus });
			}
			return;
		}
		case "resync": {
			patchItem({
				status: freshStatus,
				commitSettleStatus: "idle",
			});
			// Per-uuid lane ordering is the concurrency story for edits made during the wait: reset and
			// re-push land on the SAME investment lane in this order, so first the server-side virtual copy
			// is re-bound to the just-settled real source, then the CURRENT client composition is re-pushed
			// (syncComposition — also on this uuid's lane — reads the live item when its task runs) so
			// those edits are restored on top of the fresh baseline rather than lost.
			resetComposition(ctx, virtualUuid).catch(() => {
				// Surfacing is owned by the re-push below (toast + save-status on failure).
			});
			void syncComposition(ctx, virtualUuid);
			return;
		}
		case "abort-with-toast": {
			platformToast({ icon: "Icon-full-error", severity: "error", children: SETTLE_ERROR_TOAST });
			patchItem({ status: freshStatus, commitSettleStatus: "idle" });
			return;
		}
		case "abort": {
			patchItem({ status: freshStatus, commitSettleStatus: "idle" });
			return;
		}
		default: {
			unreachable(action);
		}
	}
}
